import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../config/api';

export default function CategoryList() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', name_ar: '', parent_id: '' });
  const [error, setError] = useState('');

  const { data: categories = [], isLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get('/api/categories').then(r => r.data),
  });

  const saveMutation = useMutation({
    mutationFn: (data) => editing
      ? api.put(`/api/categories/${editing._id}`, data)
      : api.post('/api/categories', data),
    onSuccess: () => {
      queryClient.invalidateQueries(['categories']);
      setShowForm(false);
      setEditing(null);
      setForm({ name: '', name_ar: '', parent_id: '' });
    },
    onError: (err) => setError(err.response?.data?.error || t('error')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/api/categories/${id}`),
    onSuccess: () => queryClient.invalidateQueries(['categories']),
    onError: (err) => alert(err.response?.data?.error || t('error')),
  });

  // which category opens by default on the POS screen (only one at a time)
  const setDefaultMutation = useMutation({
    mutationFn: ({ id, value }) => api.put(`/api/categories/${id}`, { default_pos: value }),
    onSuccess: () => queryClient.invalidateQueries(['categories']),
    onError: (err) => alert(err.response?.data?.error || t('error')),
  });

  const handleEdit = (cat) => {
    setEditing(cat);
    setForm({ name: cat.name, name_ar: cat.name_ar || '', parent_id: cat.parent_id || '' });
    setShowForm(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    saveMutation.mutate({
      name: form.name,
      name_ar: form.name_ar,
      parent_id: form.parent_id || null,
    });
  };

  const getParentName = (parent_id) => {
    const parent = categories.find(c => c._id === parent_id);
    return parent ? parent.name : '—';
  };

  return (
    <div>
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800">{t('categories')}</h2>
        <button
          onClick={() => { setShowForm(true); setEditing(null); setForm({ name: '', name_ar: '', parent_id: '' }); }}
          className="text-white text-sm font-medium px-4 py-2 rounded-lg" style={{ background: '#2e9f86' }}
        >
          + {t('add')}
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            {editing ? t('edit') : t('add')} {t('categories')}
          </h3>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('name')} (EN) *</label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('name')} (AR)</label>
              <input
                type="text"
                value={form.name_ar}
                onChange={e => setForm({ ...form, name_ar: e.target.value })}
                dir="rtl"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Parent Category</label>
              <select
                value={form.parent_id}
                onChange={e => setForm({ ...form, parent_id: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="">— None (Top Level) —</option>
                {categories.filter(c => !editing || c._id !== editing._id).map(c => (
                  <option key={c._id} value={c._id}>{c.name}</option>
                ))}
              </select>
            </div>
            {error && <div className="md:col-span-3 text-red-600 text-sm">{error}</div>}
            <div className="md:col-span-3 flex gap-3">
              <button
                type="submit"
                disabled={saveMutation.isPending}
                className="text-white text-sm font-medium px-6 py-2 rounded-lg disabled:opacity-50" style={{ background: '#2e9f86' }}
              >
                {saveMutation.isPending ? t('loading') : t('save')}
              </button>
              <button
                type="button"
                onClick={() => { setShowForm(false); setEditing(null); }}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium px-6 py-2 rounded-lg"
              >
                {t('cancel')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">{t('loading')}</div>
        ) : categories.length === 0 ? (
          <div className="p-8 text-center text-gray-500">{t('no_data')}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-6 py-3 font-semibold text-gray-600">{t('name')} (EN)</th>
                <th className="text-left px-6 py-3 font-semibold text-gray-600">{t('name')} (AR)</th>
                <th className="text-left px-6 py-3 font-semibold text-gray-600">Parent</th>
                <th className="text-left px-6 py-3 font-semibold text-gray-600">POS Default</th>
                <th className="text-left px-6 py-3 font-semibold text-gray-600">{t('status')}</th>
                <th className="text-left px-6 py-3 font-semibold text-gray-600">{t('actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {categories.map(cat => (
                <tr key={cat._id} className="hover:bg-gray-50">
                  <td className="px-6 py-3 font-medium text-gray-800">{cat.name}</td>
                  <td className="px-6 py-3 text-gray-600" dir="rtl">{cat.name_ar || '—'}</td>
                  <td className="px-6 py-3 text-gray-600">{cat.parent_id ? getParentName(cat.parent_id) : '—'}</td>
                  <td className="px-6 py-3">
                    <button
                      onClick={() => setDefaultMutation.mutate({ id: cat._id, value: !cat.default_pos })}
                      title="Open this category by default on the POS screen"
                      className="text-xl leading-none"
                      style={{ color: cat.default_pos ? '#f59e0b' : '#d1d5db' }}
                    >
                      {cat.default_pos ? '★' : '☆'}
                    </button>
                  </td>
                  <td className="px-6 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${cat.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {cat.status === 'active' ? t('active') : t('inactive')}
                    </span>
                  </td>
                  <td className="px-6 py-3 flex gap-2">
                    <button
                      onClick={() => handleEdit(cat)}
                      className="font-medium" style={{ color: '#2e9f86' }}
                    >
                      {t('edit')}
                    </button>
                    <button
                      onClick={() => { if (window.confirm('Delete this category?')) deleteMutation.mutate(cat._id); }}
                      className="text-red-500 hover:text-red-700 font-medium"
                    >
                      {t('delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}